import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { backendApi } from "@/api/backendApi";
import { Search, LogOut, Settings, HelpCircle, FileText, Bell, MessageSquare, CheckCircle, Briefcase, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useAuth } from "../../../context/AuthContext";

export default function DashboardNavbar() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [searchQuery, setSearchQuery] = useState("");

  const { data: notifications } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => backendApi.Notification.getAll(),
    enabled: !!user,
  });

  const { data: unreadData } = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: () => backendApi.Notification.getUnreadCount(),
    enabled: !!user,
    refetchInterval: 30000, // Refresh count every 30s
  });

  const { data: messages } = useQuery({
    queryKey: ["messages"],
    queryFn: () => backendApi.entities.Message.list(),
    enabled: !!user,
  });

  const { data: analytics } = useQuery({
    queryKey: ["dashboard", "analytics"],
    queryFn: () => backendApi.entities.Dashboard.analytics(),
    staleTime: 60000,
    enabled: !!user,
  });

  const unreadNotificationsCount = unreadData?.count || 0;
  const messagesCount = messages?.length || 0;
  const profileCompletion = analytics?.profileCompletion || 0;

  const queryClient = useQueryClient();

  const markAsReadMutation = useMutation({
    mutationFn: (id) => backendApi.Notification.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["notifications"]);
    }
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => backendApi.Notification.markAll(),
    onSuccess: () => {
      queryClient.invalidateQueries(["notifications"]);
    }
  });

  const handleNotificationClick = (notif) => {
    if (!notif.is_read) {
      markAsReadMutation.mutate(notif.id);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'applied': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'new_job': return <Briefcase className="w-4 h-4 text-orange-600" />;
      case 'view': return <Eye className="w-4 h-4 text-purple-600" />;
      default: return <Bell className="w-4 h-4 text-blue-600" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'applied': return 'bg-green-100';
      case 'new_job': return 'bg-orange-100';
      case 'view': return 'bg-purple-100';
      default: return 'bg-blue-100';
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/jobs/auth");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/jobs/list?keyword=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <nav className="bg-white border-b sticky top-0 z-50">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">

          {/* 🔥 Updated Logo (Same as Main Website) */}
          <div className="flex items-center gap-8">
            <Link to="/jobs/dashboard" className="flex items-center gap-3 group">
              <div className="relative">
                <img
                  src="/logo 2.jpg"
                  alt="WebIdeal Logo"
                  className="w-11 h-11 object-contain transform group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div>
                <span className="text-xl font-bold text-[#0F172A]">WebIdeal</span>
                <p className="text-[9px] text-gray-500 -mt-1 font-medium tracking-wider uppercase">
                  AI-SMART Hiring
                </p>
              </div>
            </Link>

            {/* Menu Items */}
            <div className="hidden md:flex items-center gap-7 text-sm font-semibold text-gray-600">
              <Link to="/jobs/list" className="hover:text-black cursor-pointer relative flex items-center">
                Jobs
              </Link>

              <Link to="/jobs/companies" className="hover:text-black transition-colors">
                Companies
              </Link>

              <Link to="/jobs/services" className="hover:text-black cursor-pointer relative flex items-center">
                Services
              </Link>
            </div>
          </div>

          {/* Search Bar */}
          <div className="hidden lg:flex flex-1 max-w-lg mx-8">
            <form onSubmit={handleSearch} className="relative w-full">
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search jobs here"
                className="w-full pl-4 pr-10 rounded-full border-gray-200 bg-gray-50"
              />
              <button type="submit" className="absolute right-1 top-1/2 -translate-y-1/2 bg-[#2557a7] p-1.5 rounded-full text-white">
                <Search className="w-4 h-4" />
              </button>
            </form>
          </div>
          {/* Right Icons - Notifications & Profile Dropdown */}
          <div className="flex items-center gap-6">

            {/* Notification & Messages Icons */}
            {user && (
              <div className="flex flex-row-reverse items-center gap-5 text-gray-500">
                {/* Messages Button (Navigates to /jobs/messages) */}
                <button
                  onClick={() => navigate('/jobs/messages')}
                  className="relative flex items-center justify-center hover:text-gray-900 transition-colors outline-none cursor-pointer"
                >
                  <MessageSquare className="w-5 h-5" />
                  {messagesCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white border border-white">
                      {messagesCount}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="relative flex items-center justify-center hover:text-gray-900 transition-colors outline-none cursor-pointer">
                      <Bell className="w-5 h-5" />
                      {unreadNotificationsCount > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white border border-white">
                          {unreadNotificationsCount}
                        </span>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 p-0 rounded-2xl shadow-xl mt-2 border-slate-100 pb-0 overflow-hidden">
                    <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                      <h4 className="font-semibold text-slate-900">Notifications</h4>
                      {unreadNotificationsCount > 0 && (
                        <span
                          onClick={() => markAllReadMutation.mutate()}
                          className="text-xs text-[#2557a7] font-medium cursor-pointer hover:underline"
                        >
                          Mark all as read
                        </span>
                      )}
                    </div>
                    <div className="max-h-[320px] overflow-y-auto">
                      {notifications && notifications.length > 0 ? (
                        notifications.slice(0, 6).map((notif, i) => (
                          <div
                            key={notif.id || i}
                            onClick={() => handleNotificationClick(notif)}
                            className={`p-4 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${!notif.is_read ? 'bg-blue-50/30' : ''}`}
                          >
                            <div className="flex gap-3">
                              <div className={`w-8 h-8 rounded-full ${getNotificationColor(notif.type)} flex items-center justify-center shrink-0`}>
                                {getNotificationIcon(notif.type)}
                              </div>
                              <div>
                                <p className="text-sm text-slate-800 font-medium leading-snug">{notif.message}</p>
                                <p className="text-[11px] text-slate-400 mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-8 text-center text-sm text-slate-500 flex flex-col items-center">
                          <Bell className="w-8 h-8 text-slate-300 mb-2" />
                          <p>No new notifications</p>
                        </div>
                      )}
                    </div>
                    <div className="p-3 text-center border-t border-slate-100 bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer">
                      <button
                        onClick={() => navigate("/jobs/notifications")}
                        className="text-sm font-semibold text-[#2557a7]"
                      >
                        View all notifications
                      </button>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

              </div>
            )}

            {!user ? (
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  onClick={() => navigate("/jobs/auth")}
                  className="text-sm font-semibold text-gray-600 hover:text-[#2557a7]"
                >
                  Login
                </Button>
                <Button
                  onClick={() => navigate("/jobs/auth", { state: { view: "register" } })}
                  className="bg-[#2557a7] hover:bg-[#1e4687] text-white px-6 rounded-full text-sm font-semibold shadow-md shadow-blue-100"
                >
                  Register
                </Button>
              </div>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <div className="flex items-center gap-2 cursor-pointer border border-[#2557a7] text-[#2557a7] rounded-full px-4 py-1.5 hover:bg-blue-50 transition-colors">
                    <span className="text-sm font-semibold truncate max-w-[100px]">
                      {(user?.name || user?.full_name || "").split(" ")[0]}
                    </span>
                  </div>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" className="w-80 p-0 rounded-2xl shadow-xl mt-2 border-slate-100 pb-2">
                  {/* Header Section */}
                  <div className="p-5 bg-gradient-to-b from-slate-50 to-white rounded-t-2xl border-b border-slate-100">
                    <div className="flex gap-4 items-start">
                      <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-lg shrink-0">
                        {(user?.name || user?.full_name || "U").charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-900 truncate">
                          {user?.name || user?.full_name || ""}
                        </h4>
                        <p className="text-sm text-slate-500 truncate mb-1">
                          {user?.current_company || "Not Mentioned"}
                        </p>

                        {/* Profile Progress */}
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-amber-500 rounded-full transition-all duration-1000"
                              style={{ width: `${profileCompletion}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-amber-600">{profileCompletion}%</span>
                        </div>
                      </div>
                    </div>
                    <Link to="/jobs/profile" className="inline-block mt-4 text-sm font-semibold text-[#2557a7] hover:text-[#1e4687] hover:underline">
                      View & Update Profile
                    </Link>
                  </div>

                  {/* Performance Section */}
                  <div className="p-5 border-b border-slate-100">
                    <div className="flex justify-between items-end mb-4">
                      <h5 className="font-semibold text-slate-800 text-sm">Your profile performance</h5>
                      <span className="text-xs text-slate-500">Last 90 days</span>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                        <p className="text-2xl font-bold text-[#2557a7] mb-1">0</p>
                        <p className="text-xs text-slate-600 font-medium line-clamp-2 h-8">Search Appearances</p>
                        <button className="text-[11px] font-semibold text-slate-500 hover:text-[#2557a7] mt-1">View all</button>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                        <p className="text-2xl font-bold text-[#2557a7] mb-1">0</p>
                        <p className="text-xs text-slate-600 font-medium line-clamp-2 h-8">Recruiter Actions</p>
                        <button className="text-[11px] font-semibold text-slate-500 hover:text-[#2557a7] mt-1">View all</button>
                      </div>
                    </div>
                  </div>

                  {/* Links Section */}
                  <div className="py-2">
                    <DropdownMenuItem className="py-2.5 px-5 cursor-pointer text-slate-700 hover:bg-slate-50 focus:bg-slate-50 font-medium text-sm">
                      <FileText className="w-4 h-4 mr-3 text-slate-400" />
                      Blog
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onClick={() => navigate("/settings")}
                      className="py-2.5 px-5 cursor-pointer text-slate-700 hover:bg-slate-50 focus:bg-slate-50 font-medium text-sm"
                    >
                      <Settings className="w-4 h-4 mr-3 text-slate-400" />
                      Settings
                    </DropdownMenuItem>

                    <DropdownMenuItem className="py-2.5 px-5 cursor-pointer text-slate-700 hover:bg-slate-50 focus:bg-slate-50 font-medium text-sm">
                      <HelpCircle className="w-4 h-4 mr-3 text-slate-400" />
                      FAQs
                    </DropdownMenuItem>

                    <div className="h-px bg-slate-100 my-2 mx-4" />

                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="py-2.5 px-5 cursor-pointer text-red-600 hover:bg-red-50 focus:bg-red-50 font-medium text-sm focus:text-red-700"
                    >
                      <LogOut className="w-4 h-4 mr-3 text-red-500" />
                      Logout
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

        </div>
      </div>
    </nav>
  );
}
